import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

export interface EmailPreviewSnapshot {
  id: string;
  previewId: string;
  htmlContent: string;
  subject: string;
  templateName?: string;
  userId: string;
  workspaceId?: string;
  campaignId?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface CreateEmailPreviewParams {
  previewId?: string;
  htmlContent: string;
  subject: string;
  templateName?: string;
  userId: string;
  workspaceId?: string;
  campaignId?: string;
}

class EmailPreviewService {
  private readonly COLLECTION_NAME = 'email_previews';

  private generatePreviewId(): string {
    return Math.random().toString(36).substring(2, 14);
  }

  buildPreviewUrl(previewId: string, origin: string = window.location.origin): string {
    return `${origin}/email-preview/${previewId}`;
  }

  async createOrUpdatePreview(params: CreateEmailPreviewParams): Promise<{ previewId: string; url: string }> {
    const previewId = params.previewId || this.generatePreviewId();
    const docRef = doc(db, this.COLLECTION_NAME, previewId);
    const existing = params.previewId ? await getDoc(docRef) : null;

    const payload = {
      previewId,
      htmlContent: params.htmlContent,
      subject: params.subject,
      templateName: params.templateName || '',
      userId: params.userId,
      workspaceId: params.workspaceId || null,
      campaignId: params.campaignId || null,
      updatedAt: serverTimestamp(),
    };

    if (existing?.exists()) {
      await updateDoc(docRef, payload);
    } else {
      await setDoc(docRef, {
        ...payload,
        createdAt: serverTimestamp(),
      });
    }

    return {
      previewId,
      url: this.buildPreviewUrl(previewId),
    };
  }

  async getPreviewById(previewId: string): Promise<EmailPreviewSnapshot | null> {
    const docRef = doc(db, this.COLLECTION_NAME, previewId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return { id: docSnap.id, ...docSnap.data() } as EmailPreviewSnapshot;
  }
}

export const emailPreviewService = new EmailPreviewService();
